import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { X } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useDeclarationInquiryThread } from "../../../../../api/declarationInquiriesApi"
import {
  densePositionsOf,
  useDeclarationQuestions,
  useDeleteQuestion,
  useQuestionDraft,
  useSendQuestions,
  type DeclarationQuestion,
  type QuestionDraft,
} from "../../../../../api/declarationQuestionsApi"
import {
  isSupplierWarningError,
  supplierWarningNotes,
} from "../../../../../api/inquiryDraftApi"
import type { NoteOut } from "../../../../../api/notesApi"
import { useSupplierDetail } from "../../../../../api/suppliersApi"
import { SupplierWarningDialog } from "../../../../shared/notes/SupplierWarningDialog"
import { DocumentPane } from "../document/DocumentPane"
import type { PassageMark } from "../document/markSelection"
import { draftFor } from "../inquiry/inquiryDraft"
import { deriveGeltung } from "../mapping/geltungMapping"
import type { StatementCard } from "../useStudioV2State"

export interface RequestQuestionsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  declId: string
  supplierId: string | null
  nameOf: (framework: string) => string
  /** The exact navigation `StudioV2FooterBar`'s own `handleAnfordern` does
   *  today. The secondary "Fragebogen anfordern" action reuses it verbatim
   *  rather than duplicating the supplier-gate check and the search params
   *  it builds. */
  onQuestionnaireRequest: () => void
  /** Which viewer the right pane opens (the SAME dispatch `StudioV2Layout`
   *  hands its own `DocumentPane`). Defaults to `false` (PDF): a workbook
   *  declaration cannot carry marked passages in the first place (mark mode
   *  is hidden there), so this only matters for the plain page render. */
  isWorkbook?: boolean
  fileName?: string
  /** Every statement card the declaration currently holds, across every
   *  Verordnung (TUR-1211): the draft-endpoint fallback body (`buildTemplateBody`)
   *  uses it to look up each collected question's own statement and open with
   *  that statement's `draftFor` paragraph. The dialog has no state of its
   *  own to derive this from (`StatementCards` live in `useStudioV2State`,
   *  one layer up), so the parent (`StudioV2FooterBar`/`StudioV2Layout`)
   *  hands it in rather than this dialog reading it itself. Defaults to `[]`:
   *  every caller/test predating this prop keeps the old flat line format,
   *  since no statement id can then resolve to a card. */
  cards?: StatementCard[]
}

type Translate = (
  key: TranslationKeys,
  v?: Record<string, string | number>,
) => string

/** Send-endpoint 409 codes mapped to a translated, reviewer-facing message.
 *  Any other/unknown code (or a bare network failure) falls back to the
 *  generic key, never the server's own English developer prose. */
const SEND_ERROR_KEYS: Record<string, TranslationKeys> = {
  NO_QUESTIONS: "MC_DECL_V2_REQ_ERR_NO_QUESTIONS",
  QUESTIONS_SEND_FAILED: "MC_DECL_V2_REQ_ERR_SEND_FAILED",
  MARKED_PDF_TOO_LARGE: "MC_DECL_V2_REQ_ERR_PDF_TOO_LARGE",
  INVALID_RECIPIENT: "MC_API_ERROR_INVALID_RECIPIENT",
}

/** Only this code's failure is a race/transient send hiccup worth a
 *  same-payload retry; the others (no drafts, an oversized attachment) need
 *  the reviewer to change something first, so no retry action is offered. */
const RETRYABLE_SEND_ERROR = "QUESTIONS_SEND_FAILED"

// Module-level, not `= []` in the destructuring default: a literal there
// allocates a NEW array every render a caller omits `cards`, which would
// needlessly bust any memoized deps or effects reading this prop.
const NO_CARDS: StatementCard[] = []

/** The backend still wraps the numbered list in these marker lines today
 *  (`mc_declaration_questions.py`'s `QUESTIONS_START`/`QUESTIONS_END`), so
 *  `send` can tell a client-edited body already contains the list apart from
 *  one that doesn't. The composer must never show the raw markers to the
 *  reviewer (W5-1 defect 2): `send` still prepends its own list when the
 *  markers are gone, so stripping them here is safe. A no-op once the backend
 *  stops emitting them. */
const QUESTION_MARKER_LINES = new Set(["[[QUESTIONS]]", "[[/QUESTIONS]]"])

function stripQuestionMarkerLines(body: string): string {
  return body
    .split("\n")
    .filter((line) => !QUESTION_MARKER_LINES.has(line.trim()))
    .join("\n")
}

/** Groups items by framework in the order they first appear, for the
 *  read-only numbered list under the composer body and the right pane's own
 *  grouped list. */
function groupByFramework(
  items: DeclarationQuestion[],
): { framework: string | null; items: DeclarationQuestion[] }[] {
  const order: (string | null)[] = []
  const map = new Map<string | null, DeclarationQuestion[]>()
  for (const item of items) {
    const key = item.framework
    if (!map.has(key)) {
      map.set(key, [])
      order.push(key)
    }
    map.get(key)?.push(item)
  }
  return order.map((framework) => ({
    framework,
    items: map.get(framework) ?? [],
  }))
}

/** "Aussage {i}" for a free-text item numbers the item's STATEMENT within
 *  its own framework (first-seen order): the same scoping every other
 *  Studio V2 surface uses for a card's display number (`MC_DECL_V2_UNIT_LABEL`
 *  in `UnitCard.tsx`/`StudioV2Layout.tsx`), not a flat count across every
 *  framework. Keyed on `framework::statementId` since two different
 *  frameworks never share a statement id, but the explicit framework key
 *  keeps the numbering scoped even if that ever changed. */
function statementIndexesByFramework(
  items: DeclarationQuestion[],
): Map<string, number> {
  const perFramework = new Map<string, number>()
  const indexes = new Map<string, number>()
  for (const item of items) {
    if (!item.statementId) continue
    const key = `${item.framework ?? ""}::${item.statementId}`
    if (indexes.has(key)) continue
    const fwKey = item.framework ?? ""
    const next = (perFramework.get(fwKey) ?? 0) + 1
    perFramework.set(fwKey, next)
    indexes.set(key, next)
  }
  return indexes
}

/** One numbered line, same wording for the left read-only preview, the right
 *  grouped list AND the client-built template fallback (W5-1 defects 4/5):
 *  a marked passage cites its page and quote (cut to 200 chars, the same
 *  bound the backend's own `_item_line` uses), everything else names its
 *  framework and statement number instead.
 *
 *  `n` is the item's DENSE rank (`positionIndexes`), not its raw stored
 *  `position`: a deleted question leaves a gap (1, 2, 4 for three
 *  survivors), and every numbered surface here must renumber to 1, 2, 3
 *  like the backend's own mail list already does (live test L5, wave 5). */
function formatItemLine(
  item: DeclarationQuestion,
  nameOf: (framework: string) => string,
  translate: Translate,
  statementIndexes: Map<string, number>,
  positionIndexes: Map<string, number>,
): string {
  const n = positionIndexes.get(item.id) ?? item.position
  if (item.page != null && item.quote) {
    return translate("MC_DECL_V2_REQUEST_LINE_MARKED", {
      n,
      page: item.page,
      quote: item.quote.slice(0, 200),
      question: item.question,
    })
  }
  const framework = item.framework ? nameOf(item.framework) : ""
  const key = item.statementId
    ? `${item.framework ?? ""}::${item.statementId}`
    : null
  const i = key ? (statementIndexes.get(key) ?? 1) : 1
  return translate("MC_DECL_V2_REQUEST_LINE_FREE_TEXT", {
    n,
    framework,
    i,
    question: item.question,
  })
}

/** One statement's own bucket in the fallback body below: its `draftFor`
 *  paragraph (when it asks anything) followed by its own collected
 *  questions, in the order they first appear. A `loose` item has no
 *  statement id, or one `cards` does not resolve (a marked-passage-only
 *  question, or any caller predating the `cards` prop): it keeps today's
 *  plain line, on its own, exactly where it fell in the original order. */
type TemplateBucket =
  | { kind: "statement"; card: StatementCard; items: DeclarationQuestion[] }
  | { kind: "loose"; item: DeclarationQuestion }

function bucketByStatement(
  items: DeclarationQuestion[],
  cardById: Map<string, StatementCard>,
): TemplateBucket[] {
  const buckets: TemplateBucket[] = []
  const bucketIndexByCardId = new Map<string, number>()
  for (const item of items) {
    const card = item.statementId ? cardById.get(item.statementId) : undefined
    if (!card) {
      buckets.push({ kind: "loose", item })
      continue
    }
    const existingIndex = bucketIndexByCardId.get(card.id)
    if (existingIndex === undefined) {
      bucketIndexByCardId.set(card.id, buckets.length)
      buckets.push({ kind: "statement", card, items: [item] })
    } else {
      const bucket = buckets[existingIndex]
      if (bucket.kind === "statement") bucket.items.push(item)
    }
  }
  return buckets
}

/** The Geltung `draftFor` reads for the fallback body: the card's own
 *  PERSISTED facts (`deriveGeltung`, same as the deleted `InquiryPackage`'s
 *  own `baseGeltungOf`), never `UnitCard`'s local, unpersisted "Rückfrage"
 *  pick (`manualGeltung`): this dialog has no access to that per-card local
 *  state, only to `StatementCard`s themselves. A statement the reviewer
 *  picked "Rückfrage" on manually, with an otherwise-resolved scope and
 *  verdict, therefore reads as resolved here and contributes no paragraph:
 *  the same asymmetry the deleted prototype's own package carried (it read
 *  the DISPLAYED Geltung only because `UnitCard`'s composer passed it in
 *  live; this fallback only ever runs from a card list, after the fact). */
function persistedGeltungOf(card: StatementCard) {
  return deriveGeltung({
    scopeAll: card.scopeAll,
    selectedCount: card.articleNumbers.length,
    unresolvedRefs: card.unresolvedRefs,
    inquiryOpen: false,
    // A statement this very dialog is about to ask questions on reads as
    // "ask" here too (live test L2, wave 5): the drafts are persisted, so
    // the asymmetry the comment above describes now only covers a manual
    // Rückfrage pick with no question collected yet.
    draftQuestions: card.draftQuestions === true,
  })
}

/** The client-side fallback body (W5-1 defect 4): a draft-endpoint failure
 *  never leaves the reviewer with a blank composer. TUR-1211: for each
 *  statement the collected questions reference, its own `draftFor` paragraph
 *  (when it asks anything) opens that statement's block, followed by its own
 *  question lines (the SAME line formatter the read-only preview uses); a
 *  question with no resolvable statement (a marked passage, or `cards`
 *  omitted) keeps that plain line on its own, exactly like before this
 *  paragraph existed. */
function buildTemplateBody(
  items: DeclarationQuestion[],
  cards: StatementCard[],
  nameOf: (framework: string) => string,
  translate: Translate,
): string {
  const statementIndexes = statementIndexesByFramework(items)
  const positionIndexes = densePositionsOf(items)
  const cardById = new Map(cards.map((c) => [c.id, c]))
  // Point at marked passages in the attachment only when an item marks one.
  const anyMarked = items.some(
    (i) => i.page != null && !!i.quote && !!i.boxes && i.boxes.length > 0,
  )
  const lines = [
    translate(
      anyMarked
        ? "MC_DECL_V2_REQ_TEMPLATE_INTRO"
        : "MC_DECL_V2_REQ_TEMPLATE_INTRO_PLAIN",
    ),
  ]
  for (const bucket of bucketByStatement(items, cardById)) {
    if (bucket.kind === "loose") {
      lines.push(
        formatItemLine(
          bucket.item,
          nameOf,
          translate,
          statementIndexes,
          positionIndexes,
        ),
      )
      continue
    }
    // No edition catalogue here (the dialog is not handed one): a reference
    // list this statement's paragraph would otherwise name falls back to
    // "the currently applicable list" and asks which edition applies,
    // `draftFor`'s own honest reading for "no catalogue".
    const paragraph = draftFor(
      bucket.card,
      persistedGeltungOf(bucket.card),
      nameOf(bucket.card.framework),
      null,
      translate,
    )
    if (paragraph) lines.push(paragraph)
    for (const item of bucket.items) {
      lines.push(
        formatItemLine(
          item,
          nameOf,
          translate,
          statementIndexes,
          positionIndexes,
        ),
      )
    }
  }
  return lines.join("\n")
}

/**
 * The request modal (W5-1 Task 8): full-height, composer left (recipient,
 * subject, body prefilled from the AI/template draft) and the numbered list
 * of collected items grouped by framework. "Senden" fires ONE
 * declaration-scoped inquiry through `useSendQuestions`; "Fragebogen
 * anfordern" stays the existing questionnaire flow, unchanged.
 */
export function RequestQuestionsDialog({
  open,
  onOpenChange,
  declId,
  supplierId,
  nameOf,
  onQuestionnaireRequest,
  isWorkbook = false,
  fileName,
  cards = NO_CARDS,
}: RequestQuestionsDialogProps) {
  const { translate } = useTranslation()
  const { data: drafts } = useDeclarationQuestions(declId, false)
  const { data: supplier } = useSupplierDetail(supplierId)
  const draft = useQuestionDraft(declId)
  const send = useSendQuestions(declId)
  const deleteQuestion = useDeleteQuestion(declId)
  // Whether this send would thread onto the supplier's existing email
  // (TUR-1211 W5-2), moved here from AskBox: the note belongs where the mail
  // actually goes out.
  const { data: thread } = useDeclarationInquiryThread(declId)

  const [recipient, setRecipient] = useState("")
  const [subject, setSubject] = useState("")
  const [body, setBody] = useState("")
  const [source, setSource] = useState<"ai" | "template" | null>(null)
  const [draftFailed, setDraftFailed] = useState(false)
  const [sentReference, setSentReference] = useState<string | null>(null)
  const [sendFailed, setSendFailed] = useState(false)
  const [sendErrorCode, setSendErrorCode] = useState<string | null>(null)
  // The supplier's warning notes, when the server refused the send for them (MC_NOTES):
  // nothing went out, so the reviewer decides.
  const [warningNotes, setWarningNotes] = useState<NoteOut[] | null>(null)
  // The right pane's own jump (a list item's "S. {page}" button): a fresh
  // token on the same page still re-triggers the viewer's scroll, same
  // contract as `StudioV2Layout`'s own `jumpToPage`.
  const [docJumpToPage, setDocJumpToPage] = useState<{
    page: number
    token: string
  } | null>(null)
  const jumpToken = useRef(0)

  const items = drafts ?? []
  const noRecipient = recipient.trim() === ""
  // Review wave int (F2): the draft (subject/body) can still be loading, or
  // just blank, when the reviewer clicks Senden, and an immediate click then
  // sent an empty mail. Blocks Send on either: the draft mutation still in
  // flight, or a subject/body that has nothing in it after trim (the same
  // window covers a redraft in flight leaving stale-but-non-empty content,
  // which this alone would miss).
  const draftEmpty = subject.trim() === "" || body.trim() === ""
  const statementIndexes = statementIndexesByFramework(items)
  // Dense off ALL items, not just the marked-passage subset below: a
  // free-text question still occupies a slot in the same numbering the mail
  // list counts against (live test L5, wave 5).
  const positionIndexes = densePositionsOf(items)
  const marks: PassageMark[] = items
    .filter((q) => q.boxes && q.boxes.length > 0)
    .map((q) => ({
      n: positionIndexes.get(q.id) ?? q.position,
      page: q.page ?? 0,
      quote: q.quote ?? "",
      boxes: q.boxes ?? [],
      question: q.question,
    }))

  const runDraft = () => {
    setDraftFailed(false)
    draft.mutate(undefined, {
      onSuccess: (result: QuestionDraft) => {
        setSubject(result.subject)
        // The composer never shows the raw [[QUESTIONS]]/[[/QUESTIONS]]
        // marker lines (W5-1 defect 2); `send` still detects and strips them
        // (or prepends its own list block when they're gone), so this is a
        // display-only transform.
        setBody(stripQuestionMarkerLines(result.body))
        setSource(result.source)
      },
      onError: () => {
        // Never a blank composer (W5-1 defect 4): the same line formatter the
        // read-only preview uses builds the fallback body, so the reviewer
        // can still send while the draft endpoint is down.
        setDraftFailed(true)
        setBody(buildTemplateBody(items, cards, nameOf, translate))
        setSource("template")
      },
    })
  }

  // Prefill on open only, not on every drafts/supplier refetch, so an
  // in-progress edit of the composer is never silently overwritten.
  useEffect(() => {
    if (!open) return
    setRecipient(supplier?.email ?? "")
    setSentReference(null)
    setSendFailed(false)
    setSendErrorCode(null)
    setWarningNotes(null)
    runDraft()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // `acknowledgedNoteIds` is set only on the retry after the reviewer confirmed the
  // supplier's warning notes.
  const sendWith = (acknowledgedNoteIds?: string[]) => {
    setSendFailed(false)
    setSendErrorCode(null)
    send.mutate(
      {
        subject,
        body,
        recipientsTo: recipient ? [recipient] : null,
        ...(acknowledgedNoteIds ? { acknowledgedNoteIds } : {}),
      },
      {
        onSuccess: (result) => {
          setSentReference(result.reference)
          setWarningNotes(null)
          // The dialog closes right after a successful send (W5-1 defect 3);
          // drafts move to "sent" via `useSendQuestions`'s own invalidation.
          onOpenChange(false)
        },
        onError: (error) => {
          // Not a failure: the dialog asks the reviewer (a note added since the
          // last ask brings it back with the new notes).
          if (isSupplierWarningError(error)) {
            setWarningNotes(supplierWarningNotes(error))
            return
          }
          setWarningNotes(null)
          const detail = (
            error as {
              response?: {
                data?: {
                  detail?: { code?: string; underlying?: { code?: string } }
                }
              }
            }
          )?.response?.data?.detail
          // The send wraps its own failure as QUESTIONS_SEND_FAILED; an invalid
          // address is no hiccup a same-payload retry can fix, so it surfaces as itself.
          const code =
            detail?.underlying?.code === "INVALID_RECIPIENT"
              ? "INVALID_RECIPIENT"
              : detail?.code
          setSendFailed(true)
          setSendErrorCode(code ?? null)
        },
      },
    )
  }
  const handleSend = () => sendWith()

  const sendErrorKey: TranslationKeys = sendErrorCode
    ? (SEND_ERROR_KEYS[sendErrorCode] ?? "MC_DECL_V2_REQ_ERR_GENERIC")
    : "MC_DECL_V2_REQ_ERR_GENERIC"
  const canRetrySend = sendErrorCode === RETRYABLE_SEND_ERROR

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* `sv2-root` re-declared on the portaled content so studioV2.css's
          scoped tokens resolve outside the workspace root (same reasoning as
          `ArticlePickerDialog`/`SubstanceRow`'s own editor dialog), without
          it every `sv2-*` rule here (the primary Send button, the redraft
          action, the AI/template pill) silently loses its styling and
          renders as plain text (W5-1 defect 1). `onEscapeKeyDown` swallows
          the keydown after Radix's own close, so it never also reaches
          `StudioV2Layout`'s window-level Escape-to-list handler (W5-1
          defect 3). */}
      <DialogContent
        data-testid="request-questions-dialog"
        className="sv2-root flex h-[92vh] w-[95vw] max-w-[1800px] flex-col gap-0 p-0"
        onEscapeKeyDown={(e) => e.stopPropagation()}
      >
        <DialogHeader className="border-b p-4">
          <DialogTitle>{translate("MC_DECL_V2_REQUEST_TITLE")}</DialogTitle>
          {/* F12 (a11y, review wave 5): screen-reader only, silences Radix's
              "Missing `Description`" console warning; the composer's own
              labelled fields already carry the visible copy. */}
          <DialogDescription className="sr-only">
            {translate("MC_DECL_V2_REQUEST_DIALOG_DESC")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-1 overflow-hidden">
          <div
            data-testid="request-compose-column"
            className="flex w-2/5 min-w-[460px] flex-col overflow-hidden border-r"
          >
            {items.length === 0 ? (
              <p
                data-testid="request-no-questions"
                className="p-4 text-[12px] text-grey5"
              >
                {translate("MC_DECL_V2_REQUEST_NO_QUESTIONS")}
              </p>
            ) : (
              <div className="flex flex-1 flex-col gap-4 overflow-hidden p-4">
                <div className="flex flex-col gap-3">
                  <div>
                    <label htmlFor="request-recipient" className="sv2-seclbl">
                      {translate("MC_DECL_V2_REQUEST_RECIPIENT")}
                    </label>
                    <Input
                      id="request-recipient"
                      data-testid="request-recipient"
                      value={recipient}
                      onChange={(e) => setRecipient(e.target.value)}
                      className="sv2-field mt-1"
                    />
                  </div>
                  {noRecipient && (
                    <p
                      data-testid="request-missing-supplier-hint"
                      className="text-[12px] text-destructive"
                    >
                      {translate("MC_DECL_V2_SUP_REQUEST_NEEDS_SUPPLIER")}
                    </p>
                  )}
                  <div>
                    <label htmlFor="request-subject" className="sv2-seclbl">
                      {translate("MC_DECL_V2_REQUEST_SUBJECT")}
                    </label>
                    <Input
                      id="request-subject"
                      data-testid="request-subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="sv2-field mt-1"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="request-body" className="sv2-seclbl">
                      {translate("MC_DECL_V2_REQUEST_BODY")}
                    </label>
                    <div className="sv2-secsrc flex items-center gap-2">
                      {source && (
                        <span
                          data-testid="request-source-badge"
                          className="sv2-tpill"
                        >
                          {translate(
                            source === "ai"
                              ? "MC_DECL_V2_REQUEST_SOURCE_AI"
                              : "MC_DECL_V2_REQUEST_SOURCE_TEMPLATE",
                          )}
                        </span>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        data-testid="request-redraft"
                        onClick={runDraft}
                        disabled={draft.isPending}
                        className="h-auto px-2 py-1 text-[11px]"
                      >
                        {translate("MC_DECL_V2_REQUEST_REDRAFT")}
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    id="request-body"
                    data-testid="request-body"
                    rows={12}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    className="sv2-field min-h-[240px] flex-none resize-none"
                  />
                  {/* TUR-1211: the numbered list always reaches the supplier
                      regardless of what the reviewer leaves in this box,
                      the backend embeds it in both the AI and template draft
                      (`mc_declaration_questions.py`'s `questions_list_block`
                      join) and `send()` re-prepends it if an edit strips it
                      out. This line replaces the old duplicate read-only
                      list block that used to repeat it verbatim below. */}
                  <p className="text-[11px] text-grey5">
                    {translate("MC_DECL_V2_REQUEST_LIST_NOTE")}
                  </p>
                  {draftFailed && (
                    <p
                      data-testid="request-draft-error"
                      className="text-[12px] text-destructive"
                    >
                      {translate("MC_DECL_V2_REQ_DRAFT_FAILED")}
                    </p>
                  )}
                  {sendFailed && (
                    <div
                      data-testid="request-error"
                      className="flex flex-col items-start gap-1.5 text-[12px] text-destructive"
                    >
                      <p>{translate(sendErrorKey)}</p>
                      {canRetrySend && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          data-testid="request-retry"
                          disabled={send.isPending}
                          onClick={handleSend}
                        >
                          {translate("MC_DECL_V2_ASKBOX_RETRY")}
                        </Button>
                      )}
                    </div>
                  )}
                  {sentReference !== null && (
                    <p
                      data-testid="request-sent"
                      className="text-[12px] text-success"
                    >
                      {translate("MC_DECL_V2_REQUEST_SENT", {
                        reference: sentReference ?? "",
                      })}
                    </p>
                  )}
                </div>
                {/* ONE question list (TUR-1211 restyle): the old left-column
                    read-only preview and this right-column actionable list
                    used to repeat the same numbered lines twice on screen.
                    This is now the only rendering, still grouped by
                    framework for the SAME order/dense-numbering the mail
                    body uses (`groupByFramework`/`positionIndexes`), just
                    laid out as rows instead of a joined sentence per item. */}
                <div
                  data-testid="request-doc-item-list"
                  className="sv2-qlist min-h-0 flex-1 overflow-y-auto"
                >
                  {groupByFramework(items).flatMap(
                    ({ framework, items: group }) =>
                      group.map((q) => {
                        const n = positionIndexes.get(q.id) ?? q.position
                        // Jump availability is `page != null` alone, the SAME
                        // condition the old separate jump Button used, never
                        // requiring a quote too. `hasQuote` only decides
                        // whether the italic quote line renders below.
                        const hasPage = q.page != null
                        const hasQuote = !!q.quote
                        const key = q.statementId
                          ? `${q.framework ?? ""}::${q.statementId}`
                          : null
                        const statementIndex = key
                          ? (statementIndexes.get(key) ?? 1)
                          : 1
                        const frameworkLabel = framework
                          ? nameOf(framework)
                          : translate("MC_DECL_V2_REQUEST_NO_FRAMEWORK")
                        return (
                          <div key={q.id} className="sv2-qrow">
                            <span className="sv2-qnum">{n}</span>
                            <div className="sv2-qbody">
                              <div className="sv2-qmeta">
                                <span>{frameworkLabel}</span>
                                <span>·</span>
                                {hasPage ? (
                                  <button
                                    type="button"
                                    data-testid={`request-doc-jump-${q.id}`}
                                    className="sv2-qchip"
                                    onClick={() => {
                                      jumpToken.current += 1
                                      setDocJumpToPage({
                                        page: q.page as number,
                                        token: String(jumpToken.current),
                                      })
                                    }}
                                  >
                                    {translate(
                                      "MC_DECL_V2_UNIT_PAGE_JUMP_SHORT",
                                      {
                                        page: q.page as number,
                                      },
                                    )}
                                  </button>
                                ) : (
                                  <span className="sv2-qchip">
                                    {translate("MC_DECL_V2_UNIT_LABEL", {
                                      i: statementIndex,
                                    })}
                                  </span>
                                )}
                              </div>
                              {hasQuote && (
                                <p className="sv2-qquote" title={q.quote ?? ""}>
                                  {q.quote}
                                </p>
                              )}
                              <p className="sv2-qtext">{q.question}</p>
                            </div>
                            {/* Wave 5 PO feedback: a collected question can be
                              removed again, right where it was collected.
                              Hidden entirely (not merely disabled) while a
                              send is in flight, so nothing can be deleted
                              out from under an in-progress mail. */}
                            {!send.isPending && (
                              <button
                                type="button"
                                data-testid={`request-remove-${q.id}`}
                                aria-label={translate(
                                  "MC_DECL_V2_ASKBOX_QUESTION_REMOVE",
                                )}
                                className="sv2-qremove"
                                onClick={() => deleteQuestion.mutate(q.id)}
                                disabled={deleteQuestion.isPending}
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        )
                      }),
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden p-4">
            {/* The dialog's own read-only reference view of the marked
                passages: no `onMark`/`onEvidencePick`, so no picking chip
                appears over this copy of the document. */}
            <div className="sv2-docframe flex flex-1 flex-col overflow-hidden">
              <DocumentPane
                declId={declId}
                isWorkbook={isWorkbook}
                zoom={1}
                onZoomChange={() => {}}
                jumpToPage={docJumpToPage}
                fileName={fileName}
                marks={marks}
                // This preview has no find bar or article browser of its
                // own (a read-only reference view of the marked passages):
                // the shared find-bar props are still required by
                // `DocumentPaneProps`, so they are wired to local, inert
                // state rather than left undefined.
                findTokens={[]}
                findSearch=""
                onFindTokensChange={() => {}}
                onFindSearchChange={() => {}}
              />
            </div>
          </div>
        </div>
        <DialogFooter className="items-center border-t p-4 sm:justify-between">
          <div className="min-w-0 flex-1">
            {thread?.inReply && (
              <p data-testid="request-thread-note" className="sv2-askn">
                {translate("MC_DECL_V2_ASKBOX_THREAD_NOTE")}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              data-testid="request-questionnaire"
              onClick={onQuestionnaireRequest}
            >
              {translate("MC_DECL_V2_REQUEST_QUESTIONNAIRE")}
            </Button>
            <button
              type="button"
              data-testid="request-send"
              disabled={
                items.length === 0 ||
                noRecipient ||
                draftEmpty ||
                draft.isPending ||
                send.isPending
              }
              onClick={handleSend}
              className="sv2-act sv2-primary"
            >
              {translate("MC_DECL_V2_REQUEST_SEND")}
            </button>
          </div>
        </DialogFooter>
        {warningNotes && (
          <SupplierWarningDialog
            mode="single"
            warnings={[
              {
                inquiryId: declId,
                supplierName: supplier?.name ?? "",
                notes: warningNotes,
              },
            ]}
            onConfirm={() => sendWith(warningNotes.map((note) => note.id))}
            onCancel={() => setWarningNotes(null)}
            isLoading={send.isPending}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
